<template>
  <button
    :class="{
      'seat-button': true,
      'seat-available': seat.status === 'AVAILABLE' && !isSelected,
      'seat-allocated': seat.status === 'ALLOCATED',
      'seat-selected': isSelected,
      'seat-blocked': seat.status === 'BLOCKED',
      'seat-comfort': isComfortSeat,
    }"
    :style="seatStyle"
    :disabled="seat.status !== 'AVAILABLE'"
    @click="selectSeat"
  >
    <span
      v-if="seat.status !== 'AVAILABLE'"
      class="icon-cancel"
    >
      <svg class="icon-cancel">
        <use :xlink:href="`#${cancel}`" />
      </svg>
    </span>
    <span
      v-if="isSelected"
      class="icon-checked"
    >
      <svg class="icon-checked">
        <use :xlink:href="`#${checked}`" />
      </svg>
    </span>
    <span class="sr-only">boke sete nummer {{ seat.seat_number }}</span>
    <!-- <span class="seat-number">{{ seat.seat_number }}</span> -->
    <ToolTip
      :seat="seat"
      :seatPrice="seatPrice"
      :isSelected="isSelected"
      :seatDescription="getSeatDescription(seat.property_codes || [])"
    />
  </button>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import { CarriageSeat } from '@/types/seatTypes'
  import ToolTip from '@/components/TravelPlanner/SeatReservation/ToolTip.vue'
  import cancel from '@/assets/icons/cancel.svg'
  import checked from '@/assets/icons/checked.svg'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'



  export default defineComponent({
    name: 'SeatButton',
    components: {
      ToolTip,
    },
    props: {
      seat: {
        type: Object as PropType<CarriageSeat>,
        required: true,
      },
      isSelected: {
        type: Boolean,
        default: false,
      },
      seatStyle: {
        type: Object,
        required: true,
      },
      seatPrice: {
        type: Number,
        default: 0,
      },
      serviceName: {
        type: String,
        required: true,
      },
    },
    emits: ['selectSeat'],
    setup(props, { emit }) {
      const selectSeat = () => {
        if (
          props.seat.status === 'ALLOCATED' ||
          props.seat.status === 'BLOCKED'
        ) {
          return
        }
        emit('selectSeat', props.seat)
      }
      const isComfortSeat = props.seat.property_codes?.includes('COMFORT_SEAT')

      const getSeatDescription = (propertyCodes: string[]) => {
        const seatStore = useSeatReservationStore()

        if (!propertyCodes?.length && seatStore.standardSeatProduct) {
          return seatStore.getSeatPropertyDescription('STANDARD_SEAT')
        }

        // Otherwise get descriptions from the property codes
        if (!propertyCodes?.length) return ''

        const descriptions = propertyCodes
          .map((code) => seatStore.getSeatPropertyDescription(code))
          .filter((desc) => desc)

        return descriptions.join(', ')
      }

      return {
        selectSeat,
        isComfortSeat,
        getSeatDescription,
        cancel,
        checked,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .seat-button {
    padding: 0;
    margin: 0;
    border: none;
    cursor: pointer;
    background: transparent;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    // TEMP border for positioning
    /* border: 1px solid #df26ac !important; */
  }

  /* .seat-comfort {
    width: 61px !important;
    height: 74px !important;
    border: 1px solid #2aae02 !important;
  } */

  .seat-available {
    background-color: transparent;
    border-radius: $border-radius-small;
    color: #7077a6;
    border: none;

    &:hover {
      background-color: rgba(112, 119, 166, 0.1);
    }
  }

  .seat-allocated {
    background-color: $clr-primary-50;
    border-radius: 4px;
    border: none;
    background-color: $clr-gray-transparent;
    cursor: not-allowed;
  }

  .seat-selected {
    border-radius: 4px;
    border: none;
    background-color: $clr-secondary-transparent;
    color: #ffffff;
  }

  .seat-blocked {
    background-color: #f5f5f5;
    border: 2px solid #cccccc;
    border-radius: 4px;
    opacity: 0.5;
    cursor: not-allowed;
  }

  .icon-cancel {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    color: $clr-primary-300;
    background-color: $clr-primary-50;
    border-radius: 50%;

    &:is(span) {
      border: 1px solid $clr-primary-300;
    }

    svg {
      width: 9px;
      height: 9px;
    }
  }

  .icon-checked {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    color: $clr-white;
    background-color: $clr-secondary;
    border-radius: 50%;

    &:is(span) {
      border: 1px solid $clr-secondary-800;
    }

    svg {
      width: 14px;
      height: 11px;
    }
  }

  .seat-number {
    font-size: 10px;
    line-height: 1;
  }

  .seat-price {
    font-size: 12px;
    margin-top: 20px;
  }

  .seat-button:hover {
    :deep(.tooltip) {
      visibility: visible;
    }
  }
</style>